import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CareStateStateUpdateT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { generateClientId } from '@/shared/lib';

import type { CareStateEvent, CareStateRow, CareStateUpdate } from './types.js';

export type CareStateCursor = number;

export type AcceptedUpdateReply = { type: 'acceptedUpdate'; cursor: CareStateCursor };
export type RejectedUpdateReply = { type: 'rejectedUpdate'; reason: string };
export type UpdateReply = AcceptedUpdateReply | RejectedUpdateReply;

/**
 * Options for streaming connections.
 */
export interface StreamingConnectionOptions {
  signal?: AbortSignal;
}

export interface CareStateClient<DbT extends { [K in keyof DbT]: CareStateRow[] }> {
  /**
   * Fetch the current snapshot of the CareState (server truth), plus a cursor.
   *
   * The cursor is an opaque integer: feeding it to `streamEvents(cursor)` should yield only newer events.
   */
  fetch(): Promise<[DbT, CareStateCursor]>;

  /**
   * Open a streaming connection to receive events in real-time.
   *
   * Yields only data events. Protocol events (ping, disconnect) are handled internally.
   * The generator returns cleanly when the server requests reconnection.
   * Throws StreamingError if the server sends an error.
   *
   * Callers should loop and reconnect when the generator completes.
   *
   * @param cursor - Starting cursor position
   * @param options - Options including AbortSignal for cancellation
   * @yields Data events containing CareState changes
   * @throws StreamingError if server sends an error event
   */
  streamEvents(
    cursor: CareStateCursor,
    options?: StreamingConnectionOptions
  ): AsyncGenerator<CareStateStateUpdateT, void, unknown>;

  /**
   * Submit a batch of client updates keyed by a client-generated ID.
   *
   * The server replies per-update, including a cursor when accepted.
   */
  submit(updates: Record<string, CareStateUpdate<DbT>>): Promise<Record<string, UpdateReply>>;

  /**
   * Apply events and updates to reconstruct the effective state.
   *
   * This should be implemented using `applyCareStateOperations` from this entity.
   */
  applyEventsAndUpdates(
    state: DbT,
    events: Array<CareStateEvent<DbT>>,
    updates: Array<CareStateUpdate<DbT>>
  ): DbT;

  /**
   * Debounce interval for submit scheduling. This is a backpressure knob to avoid hammering
   * slow backends and to group updates into batches.
   */
  updateDebounceInterval: number;
}

type MultiplayerState<DbT extends { [K in keyof DbT]: CareStateRow[] }> = {
  state: DbT;
  cursor: CareStateCursor;
};

type PendingUpdate<DbT extends { [K in keyof DbT]: CareStateRow[] }> = {
  id: string;
  update: CareStateUpdate<DbT>;
  /**
   * Null means "not submitted yet". Otherwise, the cursor at which the server accepted the update.
   */
  cursor: CareStateCursor | null;
};

export type CareStateStatus = 'loading' | 'ready' | 'error';

export type UseCareStateResult<DbT extends { [K in keyof DbT]: CareStateRow[] }> = {
  /**
   * The effective CareState: server snapshot + optimistic updates (local + submitted-but-not-yet-seen).
   *
   * Null while the snapshot is still loading.
   */
  state: DbT | null;
  update(update: CareStateUpdate<DbT>): void;
  status: CareStateStatus;
  lastError: string | null;
  /**
   * Minimal debug state for dev tooling and sandboxes.
   *
   * Intentionally does not expose internal IDs or full pending payloads.
   */
  debug: {
    serverCursor: CareStateCursor | null;
    pendingUpdateCount: number;
    pendingUnsubmittedCount: number;
    pendingSubmittedCount: number;
  };
};

/**
 * Debounce an async callback, ensuring only one instance runs at a time.
 *
 * If triggered while running, one additional run will be scheduled immediately after completion.
 */
function useDebouncedExclusiveAsync(callback: () => Promise<void>, delayMs: number): () => void {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<number | null>(null);

  const inFlightRef = useRef<Promise<void> | null>(null);
  const rerunRequestedRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const run = useCallback(async () => {
    if (inFlightRef.current !== null) {
      rerunRequestedRef.current = true;
      return;
    }

    const execute = async () => {
      try {
        await callbackRef.current();
      } finally {
        inFlightRef.current = null;
      }
    };

    inFlightRef.current = execute();
    await inFlightRef.current;

    if (rerunRequestedRef.current) {
      rerunRequestedRef.current = false;
      void run();
    }
  }, []);

  return useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => void run(), delayMs);
  }, [delayMs, run]);
}

// Decode streaming data event into internal CareStateEvent
function decodeStreamingDataEvent<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  dataEvent: CareStateStateUpdateT
): CareStateEvent<DbT> {
  const rawEvent = dataEvent.event;
  return {
    type: 'replace',
    table: rawEvent.table as Extract<keyof DbT, string>,
    id: rawEvent.id,
    oldValue: rawEvent.old_value as unknown as DbT[Extract<keyof DbT, string>][number] | null,
    newValue: rawEvent.new_value as unknown as DbT[Extract<keyof DbT, string>][number] | null,
  } as CareStateEvent<DbT>;
}

/**
 * Multiplayer CareState lifecycle hook.
 *
 * - Fetch server snapshot + cursor
 * - Stream events from server in real-time; apply to server state; prune acked pending updates
 * - Automatically reconnect on server disconnect
 * - Allow local updates; debounce submit in batches; record acceptance cursor/rejection
 * - Expose reconstructed effective state to UI
 */
export function useCareState<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  client: CareStateClient<DbT>
): UseCareStateResult<DbT> {
  const [status, setStatus] = useState<CareStateStatus>('loading');
  const [lastError, setLastError] = useState<string | null>(null);

  const [serverState, setServerState] = useState<MultiplayerState<DbT> | null>(null);
  const [pendingUpdates, setPendingUpdates] = useState<Array<PendingUpdate<DbT>>>([]);

  // Track current cursor for streaming - updated as events come in
  const cursorRef = useRef<CareStateCursor | null>(null);

  // Abort controller for the streaming connection
  const abortControllerRef = useRef<AbortController | null>(null);

  // Flag to signal reconnection is needed
  const [reconnectTrigger, setReconnectTrigger] = useState(0);

  // Initial fetch effect
  useEffect(() => {
    let cancelled = false;
    // Initialize loading state at the start of async fetch - intentional pattern

    setStatus('loading');

    setLastError(null);

    void client
      .fetch()
      .then(([state, cursor]) => {
        if (cancelled) return;
        setServerState({ state, cursor });
        cursorRef.current = cursor;
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLastError(err instanceof Error ? err.message : String(err));
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  // Handle a single incoming data event
  const handleIncomingEvent = useCallback(
    (event: CareStateEvent<DbT>, newCursor: CareStateCursor) => {
      setServerState((prev) => {
        if (prev === null) {
          return null;
        }
        return {
          state: client.applyEventsAndUpdates(prev.state, [event], []),
          cursor: newCursor,
        };
      });

      // Update our cursor ref for streaming reconnection
      cursorRef.current = newCursor;

      // Any update whose acceptance cursor is <= the new server cursor is now obsolete.
      setPendingUpdates((prev) =>
        prev.filter(({ cursor }) => cursor === null || cursor > newCursor)
      );
    },
    [client]
  );

  // Streaming connection effect
  const serverStateNull = serverState === null;
  useEffect(() => {
    // Don't start streaming until we have initial state
    if (serverStateNull) {
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const runStream = async () => {
      const cursor = cursorRef.current;
      if (cursor === null) {
        return;
      }

      try {
        // Stream yields only data events; protocol events handled internally
        for await (const dataEvent of client.streamEvents(cursor, {
          signal: abortController.signal,
        })) {
          // Check for abort before processing
          if (abortController.signal.aborted) {
            return;
          }

          // Decode the streaming event and use the cursor from the server
          const event = decodeStreamingDataEvent<DbT>(dataEvent);
          handleIncomingEvent(event, dataEvent.cursor);
        }

        // Stream ended cleanly (disconnect event was received) - trigger reconnect
        if (!abortController.signal.aborted) {
          setReconnectTrigger((prev) => prev + 1);
        }
      } catch (err: unknown) {
        // Errors from streaming are real errors (abort is handled internally)
        setLastError(err instanceof Error ? err.message : String(err));
        // Wait before reconnecting after error
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (!abortController.signal.aborted) {
          setReconnectTrigger((prev) => prev + 1);
        }
      }
    };

    void runStream();

    return () => {
      abortController.abort();
      abortControllerRef.current = null;
    };
  }, [client, serverStateNull, handleIncomingEvent, reconnectTrigger]);

  const submitPendingUpdates = useDebouncedExclusiveAsync(
    useCallback(async () => {
      const updatesToSubmit: Record<string, CareStateUpdate<DbT>> = {};

      for (const { id, update, cursor } of pendingUpdates) {
        if (cursor !== null) {
          continue;
        }
        updatesToSubmit[id] = update;
      }

      if (Object.keys(updatesToSubmit).length === 0) {
        return;
      }

      const submittedUpdates = await client.submit(updatesToSubmit);

      setPendingUpdates((prev) => {
        const next: Array<PendingUpdate<DbT>> = [];
        for (const pendingUpdate of prev) {
          const reply = submittedUpdates[pendingUpdate.id];
          if (reply === undefined) {
            next.push(pendingUpdate);
            continue;
          }

          if (reply.type === 'acceptedUpdate') {
            next.push({ ...pendingUpdate, cursor: reply.cursor });
            continue;
          }

          // Rejected updates are dropped, but we surface a message for UX.
          setLastError(`Update ${pendingUpdate.id} was rejected: ${reply.reason}`);
        }
        return next;
      });
    }, [client, pendingUpdates]),
    // Match multiplayer demo behavior: batching updates is "okay to debounce".
    1000
  );

  const update = useCallback(
    (nextUpdate: CareStateUpdate<DbT>) => {
      const id = generateClientId();
      setPendingUpdates((prev) => [...prev, { id, update: nextUpdate, cursor: null }]);
      submitPendingUpdates();
    },
    [submitPendingUpdates]
  );

  const reconstructedState = useMemo(() => {
    if (serverState === null) {
      return null;
    }
    return client.applyEventsAndUpdates(
      serverState.state,
      [],
      pendingUpdates.map(({ update }) => update)
    );
  }, [client, pendingUpdates, serverState]);

  const debug = useMemo(() => {
    const pendingUnsubmittedCount = pendingUpdates.reduce(
      (acc, u) => acc + (u.cursor === null ? 1 : 0),
      0
    );
    return {
      serverCursor: serverState?.cursor ?? null,
      pendingUpdateCount: pendingUpdates.length,
      pendingUnsubmittedCount,
      pendingSubmittedCount: pendingUpdates.length - pendingUnsubmittedCount,
    };
  }, [pendingUpdates, serverState?.cursor]);

  return { state: reconstructedState, update, status, lastError, debug };
}
